import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import {
    PasswordStrength,
    PasswordStrengthResult,
    PasswordStrengthService
} from '../../services/password-strength.service';

@Component({
  selector: 'app-password-strength-indicator',
  templateUrl: './password-strength-indicator.component.html',
  styleUrls: ['./password-strength-indicator.component.scss']
})
export class PasswordStrengthIndicatorComponent implements OnChanges, OnDestroy {
  @Input() password: string = '';
  @Input() showGuidance: boolean = true;
  @Input() confirmationToken?: string;
  @Input() resetPasswordToken?: string;

  strengthResult: PasswordStrengthResult | null = null;
  guidanceMessage: string;
  PasswordStrength = PasswordStrength;
  
  private passwordSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(private passwordStrengthService: PasswordStrengthService) {
    this.guidanceMessage = this.passwordStrengthService.getGuidanceMessage();
    
    // Set up debounced password strength checking
    this.passwordSubject.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(password => {
        if (!password) {
          return this.passwordStrengthService.checkPasswordStrength('');
        }
        
        const context: any = {};
        if (this.confirmationToken) {
          context.confirmationToken = this.confirmationToken;
        }
        if (this.resetPasswordToken) {
          context.resetPasswordToken = this.resetPasswordToken;
        }
        
        return this.passwordStrengthService.checkPasswordStrength(password, context);
      })
    ).subscribe(result => {
      this.strengthResult = result;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['password']) {
      this.passwordSubject.next(this.password || '');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getStrengthLabel(): string {
    if (!this.strengthResult) return '';

    switch (this.strengthResult.strength) {
      case PasswordStrength.WEAK:
        return 'Weak';
      case PasswordStrength.MODERATE:
        return 'Moderate';
      case PasswordStrength.STRONG:
        return 'Strong';
      default:
        return '';
    }
  }

  getStrengthClass(): string {
    if (!this.strengthResult) return '';
    return `strength-${this.strengthResult.strength}`;
  }

  getProgressWidth(): string {
    if (!this.strengthResult) return '0%';

    switch (this.strengthResult.strength) {
      case PasswordStrength.WEAK:
        return '33%';
      case PasswordStrength.MODERATE:
        return '66%';
      case PasswordStrength.STRONG:
        return '100%';
      default:
        return '0%';
    }
  }
} 